<template>
  <spinner v-if="loading" />
  <div v-else class="screen-container">
    <v-layout column>
      <v-flex class="title-screen" xs12>
        <span>{{ adminText('workflowsAll.tickets_5c622a5', 'Tickets') }}</span>
      </v-flex>
      <v-select
        v-model="selectedPerformanceId"
        :items="workflowUi(performances)"
        item-text="from"
        item-value="id"
        :label="adminText('workflowsAll.ticket_types_b974d11', 'Ticket types')"
        box
        @change="handlePerformanceChange"
      />
      <v-flex x4>
        <div class="title-description attendee-table-description" />
      </v-flex>
      <div class="attendee_table">
        <div
          v-if="tickets.length === 0"
          class="no-attendees-message-container border__left border__right"
        >
          <span class="no-attendees-message">{{ adminText('workflowsAll.no_matching_records_found_0c7f0a6', 'No matching records found.') }}</span>
        </div>
        <table-list
          :hide-checkbox="true"
          :event-id="null"
          :organization-slug="organizationSlug"
          :selected.sync="selected"
          :attendees="tickets"
          body-style="grid-auto-columns: 50px minmax(150px, 300px) !important;"
          :visible-fields="visibleFields"
          :pagination="pagination"
          :attendee-fields="attendeeFields"
          @sort="handleSort($event)"
          @click="handleTicketClick($event)"
        />
        <table-pagination
          :pagination="pagination"
          :total-attendees="totalTicketCount"
          @change="handlePagination($event)"
          @changePerPage="handlePagination($event)"
        />
      </div>
    </v-layout>
    <edit-ticket-modal
      v-if="showEditModal && selectedTicket"
      :ticket="selectedTicket"
      :organization-slug="organizationSlug"
      @close="showEditModal = false"
      @saved="handleTicketSaved"
    />
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import TableList from '../accreditations/attendee-table/list.vue'
import TablePagination from '../accreditations/attendee-table/pagination.vue'
import EditTicketModal from './EditTicketModal.vue'
import { GET_EVENT_AND_ORDER_ITEMS_BY_EVENT_TEMPLATE_ID } from '@/graphql/event.graphql'
import { GET_TICKET_SELLERS } from '@/graphql/user.graphql'
import moment from 'moment'

export default {
  mixins: [WorkflowDisplay],
  name: 'tickets-list',
  components: {
    TableList,
    TablePagination,
    EditTicketModal,
  },
  props: {
    eventTemplateId: String,
    user: Object,
    organizationSlug: String,
  },
  data() {
    const { pagination } = this.getInitialFromQueryParams()

    return {
      attendees: [],
      totalTicketCount: 0,
      attendeeFields: {},
      selectedPerformanceId: null,
      visibleFields: [
        'Ticket name',
        'Sold',
        'Price',
        'Start date',
        'End date',
        'Min. - max. per order',
        // 'Sales channel',
      ],
      selected: [],
      pagination,
      orderItems: [],
      performances: [],
      form: { sellers: [] },
      availableSellers: [],
      tickets: [],
      loading: true,
      showEditModal: false,
      selectedTicket: null,
    }
  },
  apollo: {
    getEventsAndOrderItemsByEventTemplateId: {
      query: GET_EVENT_AND_ORDER_ITEMS_BY_EVENT_TEMPLATE_ID,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
          eventTemplateId: this.eventTemplateId,
        }
      },
      manual: true,
      async result({ data, loading }) {
        if (!loading) {
          const {
            data: { getTicketSellers: allTicketSellers },
          } = await this.$apollo.query({ query: GET_TICKET_SELLERS })
          this.availableSellers = allTicketSellers
          this.performances = data.getEventsAndOrderItemsByEventTemplateId
            .sort((a, b) => new Date(b.from) - new Date(a.from))
            .map(event => ({ ...event, from: moment(event.from).format('DD.MM.YYYY HH:mm') }))
          this.orderItems = data.getEventsAndOrderItemsByEventTemplateId
            .map(event => event.orderItems)
            .flat()

          if (this.performances.length > 0) {
            if (!this.selectedPerformanceId) {
              this.selectedPerformanceId = this.performances[0].id
            }
            this.modifyTicketsData(this.selectedPerformanceId)
          }
          this.loading = false
        }
      },
    },
  },
  methods: {
    modifyTicketsData(id) {
      const orderItems = this.orderItems.filter(oi => oi.eventId === id)
      this.tickets = orderItems.map(ticket => {
        return {
          ...ticket,
          isSoldOut: ticket.quantity === ticket.initialQuantity,
          sold: ticket.initialQuantity - ticket.quantity,
          rawFrom: ticket.from,
          rawTo: ticket.to,
          from: moment(ticket.from).format('DD.MM.YYYY'),
          to: moment(ticket.to).format('DD.MM.YYYY'),
          'min. - max. per order': this.buildMinMaxPerOrder(ticket),
        }
      })
      this.totalTicketCount = this.tickets.length
    },
    handlePerformanceChange(id) {
      this.modifyTicketsData(id)
    },
    async handlePagination({ page, per_page }) {
      const { page: currentPage, per_page: currentPerPage } = this.pagination
      if (currentPage > page && currentPage === 1) return
      if (page > currentPage && !(this.totalTicketCount - currentPerPage * currentPage > 0)) return

      this.pagination = {
        ...this.pagination,
        per_page: per_page === 'All' ? -1 : per_page,
        page: page,
      }

      this.addPaginationToRoute(this.pagination)
    },
    async handleSort(sortBy) {
      const { per_page, page, sortBy: thisSortBy, descending } = this.pagination
      const pagination = {
        page,
        per_page,
        sortBy,
        descending: sortBy === thisSortBy ? !descending : false,
      }
      this.addPaginationToRoute(pagination)
      this.pagination = pagination
    },
    getInitialFromQueryParams() {
      const { per_page, page, sortBy, descending } = this.$route.query

      return {
        pagination: {
          page: parseInt(page, 10) || 1,
          per_page: parseInt(per_page, 10) || 25,
          sortBy: sortBy,
          descending: descending | false,
        },
      }
    },
    addPaginationToRoute({ page, per_page, sortBy, descending }) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          per_page,
          page,
          sortBy,
          descending,
        },
      })
    },
    handleTicketClick(id) {
      this.selectedTicket = this.tickets.find(t => t.id === id) || null
      if (this.selectedTicket) {
        this.showEditModal = true
      }
    },
    handleTicketSaved() {
      this.$apollo.queries.getEventsAndOrderItemsByEventTemplateId.refetch()
    },
    buildMinMaxPerOrder(ticket) {
      if (!ticket || !ticket.itemMeta) return ''
      if (!ticket.itemMeta.min && !ticket.itemMeta.max) return '0 - 10'
      if (ticket.itemMeta.min === ticket.itemMeta.max) return ticket.itemMeta.min

      return (ticket.itemMeta.min || '0') + ' - ' + (ticket.itemMeta.max || '10')
    },
  },
}
</script>

<style scoped>
.attendee-table-description {
  max-width: 900px;
  margin-bottom: 28px;
}
.no-attendees-message-container {
  display: flex;
  align-items: center;
  justify-content: center;
}
.no-attendees-message {
  color: #979797;
  padding: 20px;
}
.border__left {
  border-left: 2px solid #f5f5f5;
}
.border__right {
  border-right: 2px solid #f5f5f5;
}
ul {
  list-style-type: none;
}

.err-line {
  margin-bottom: 8px;
}

.err-line span {
  font-weight: bold;
}
.title-screen {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}
@media screen and (max-width: 800px) {
  .title-screen {
    display: none;
  }
  .screen-container {
    margin-top: 1.5rem;
  }
}
.add-symbol {
  font-size: 1.875rem;
  margin-right: 0.8125rem;
  font-weight: 200;
}
.delete-message {
  position: fixed;
  left: 0px;
  top: 0px;
  right: 0px;
  bottom: 0px;
  background: white;
  opacity: 80%;
  z-index: 500;
}
.delete-wrapper {
  background-color: rgba(0, 0, 0, 0.8);
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 100;
  content: '';
  position: fixed;
}
.delete-wrapper > .delete-container {
  width: 43.75rem;
  background: white;
  position: fixed;
  max-width: 80%;
  opacity: none;
  text-align: left;
  border-radius: 0.5rem;
  top: 25%;
  left: 50%;
  filter: drop-shadow(0px 0px 24px rgba(51, 51, 51, 0.2));
  padding: 1.25rem;
  transform: translate(-50%, 0);
}
.delete-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 1.25rem;
  letter-spacing: -0.03em;
  color: #0277bd;
  margin-bottom: 1.5rem;
}
.delete-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  color: #666666;
}
.delete-buttons {
  display: flex;
  justify-content: flex-end;
}
.delete-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.close-icon-style:hover {
  cursor: pointer;
}
.ticket-sales-channels {
  margin-top: 2rem !important;
  column-gap: 1.5rem;
  display: flex;
}
.sales-channels {
  width: 330px;
}
@media screen and (max-width: 1175px) {
  .ticket-sales-channels {
    display: flex;
    flex-direction: column;
  }
  .sales-channels {
    width: 50%;
  }
  .select-outlets {
    width: 50%;
  }
}
</style>
